import { useCallback, useEffect, useMemo } from 'react';

import { FileEntity } from 'api/types/entity';
import { useField } from 'libs/form/fields/useField';
import { FilesUpload as BaseFilesUpload } from 'libs/ui';
import { FileExtension } from 'types/file';
import { v4 as uuidv4 } from 'uuid';

type FileField = FileEntity & { file?: File };

export interface FilesUploadFieldProps {
  name: string;
  title: string;
  fileExtensions?: FileExtension[];
  maxFiles?: number;
}

/**
 * FilesUpload Field
 */
export const FilesUpload = ({
  name,
  title,
  ...rest
}: FilesUploadFieldProps) => {
  const { helpers, meta, field } = useField<FileField[]>(name);

  const files = useMemo(() => field.value ?? [], [field.value]);

  const onAddFiles = useCallback(
    async (file: File[]) => {
      const newValues = [...(field.value ?? [])];

      await Promise.all(
        file.map(async (uploadFile) => {
          const id = uuidv4();
          newValues.push({
            id,
            name: uploadFile.name,
            createdAt: new Date(),
            size: uploadFile.size,
            file: uploadFile,
            uploadedByAdmin: true,
          });

          helpers.setValue(newValues);
        })
      );
    },
    [field.value, helpers]
  );

  const onRemoveFile = useCallback(
    (file: FileEntity) => {
      const newValues = field.value.filter((f) => f.id !== file.id);
      helpers.setValue([...newValues]);
    },
    [field.value, helpers]
  );

  useEffect(() => {
    if (files) {
      helpers.setError(undefined);
    }
  }, [files, helpers]);

  return (
    <BaseFilesUpload
      files={files}
      isError={meta.isError}
      name={name}
      title={title}
      onAddFiles={onAddFiles}
      onRemoveFile={onRemoveFile}
      {...rest}
    />
  );
};
